import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCloudSunRain } from "@fortawesome/free-solid-svg-icons";
import { faDroplet, faLocationDot, faTemperatureHalf, faUmbrella, faWind } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Result } from "antd";
import moment from "moment";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Weather from "../../Weather";

const MIN_SCALE = 0.2;
const MAX_SCALE = 4;

interface DashboardCurrentConditionsProps {
    projectInfo: any;
    data: any;
    fetchWeatherData: () => Promise<any>;
}

const DashboardCurrentConditions: React.FC<DashboardCurrentConditionsProps> = ({
    projectInfo,
    data,
    fetchWeatherData,
}) => {
    const [weatherData, setWeatherData] = React.useState<any>(data);

    const boxRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);

    const reloadDataInterval = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    React.useEffect(() => {
        setWeatherData(data);
    }, [data]);

    React.useEffect(() => {
        fetchWeatherData().then((newData) => {
            setWeatherData(newData);
        });
        reloadDataInterval.current = setInterval(
            () => {
                fetchWeatherData().then((newData) => {
                    setWeatherData(newData);
                });
            },
            5 * 60 * 1000
        ); // Update every 5 minutes

        return () => {
            if (reloadDataInterval.current) {
                clearInterval(reloadDataInterval.current);
            }
        };
    }, [projectInfo.unique_token]);

    const current = weatherData?.current;
    const today = weatherData?.daily_forecast?.[0];
    const city = projectInfo?.city;

    const weatherIcon = useMemo(() => Weather.getWeatherIcon(current?.condition ?? "Unknown"), [current?.condition]);

    // Scales the whole block (icons, logo image, gradient bar, and text together) so every
    // element keeps its designed proportion relative to the others - sized off this panel's
    // actual box (ResizeObserver), not the viewport, so it fills whatever space the Command
    // Center grid gives it instead of overflowing or overlapping.
    const fitScale = useCallback(() => {
        const box = boxRef.current;
        const content = contentRef.current;
        if (!box || !content) {
            return;
        }

        const boxWidth = box.clientWidth;
        const boxHeight = box.clientHeight;
        const contentWidth = content.scrollWidth;
        const contentHeight = content.scrollHeight;
        if (!boxWidth || !boxHeight || !contentWidth || !contentHeight) {
            return;
        }

        const nextScale = Math.min(boxWidth / contentWidth, boxHeight / contentHeight);
        setScale(Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale)));
    }, [weatherData, city]);

    useLayoutEffect(() => {
        fitScale();
    }, [fitScale]);

    useEffect(() => {
        const box = boxRef.current;
        if (!box) {
            return;
        }

        const observer = new ResizeObserver(() => fitScale());
        observer.observe(box);

        return () => observer.disconnect();
    }, [fitScale]);

    if (!current) {
        return (
            <Result
                className="w-full h-full"
                icon={<FontAwesomeIcon icon={faCloudSunRain as IconProp} size="3x" color="#FFFFFF" />}
                title={<div className="text-white">Weather currently unavailable</div>}
            />
        );
    }

    // Providers never populate precipitation_chance on `current` (only on hourly/daily
    // forecasts), so fall back to today's forecasted chance instead of always showing 0%.
    const rainChance = today?.precipitation_chance ?? current.precipitation_chance;

    const hasGust = current.wind_gust && Math.round(current.wind_gust) > Math.round(current.wind_speed);
    const windValue = `${current.wind_direction ? `${current.wind_direction} ` : ""}${Math.round(current.wind_speed)} mph`;

    const hasTempRange = typeof today?.high_temp === "number" && typeof today?.low_temp === "number";
    const tempRange = hasTempRange ? today.high_temp - today.low_temp : 0;
    const tempMarkerPct =
        hasTempRange && tempRange > 0
            ? Math.min(100, Math.max(0, ((current.temperature - today.low_temp) / tempRange) * 100))
            : 50;

    const stats = [
        { label: "Feels Like", value: `${Math.round(current.feels_like)}°`, icon: faTemperatureHalf },
        {
            label: "Wind",
            value: windValue,
            subValue: hasGust ? `Gusts to ${Math.round(current.wind_gust)} mph` : undefined,
            icon: faWind,
        },
        { label: "Humidity", value: `${Math.round(current.humidity)}%`, icon: faDroplet },
        {
            label: "Chance of Rain",
            value: rainChance != null ? `${Math.round(rainChance)}%` : "--",
            icon: faUmbrella,
        },
    ];

    return (
        <div ref={boxRef} className="h-full w-full overflow-hidden flex items-center justify-center">
            <div
                ref={contentRef}
                className="dashboard-current-conditions flex flex-col text-white gap-8 pt-[10%] pb-[15%] px-24"
                style={{ transform: `scale(${scale})`, transformOrigin: "center" }}
            >
                <div className="flex flex-col justify-center gap-8 min-h-0">
                    <div className="flex flex-row items-center justify-between gap-6">
                        <div className="flex flex-row items-center gap-6 shrink-0">
                            <FontAwesomeIcon icon={weatherIcon} className="text-plot-yellow shrink-0" size="7x" />
                            <div
                                className="text-9xl font-plot-open-sans leading-none shrink-0"
                                style={{ fontVariantNumeric: "tabular-nums" }}
                            >
                                {Math.round(current.temperature)}°
                            </div>
                        </div>
                        <div className="flex flex-col">
                            <div className="text-6xl font-plot-oswald text-neutral-200 leading-tight whitespace-nowrap">
                                {current.condition}
                            </div>
                            {city && (
                                <div className="flex flex-row items-center gap-2 text-neutral-400 mt-2 text-2xl">
                                    <FontAwesomeIcon icon={faLocationDot as IconProp} className="shrink-0" />
                                    <span className="whitespace-nowrap">{city}</span>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col items-end gap-2 shrink-0">
                            <img
                                src="/img/png/partner_logos/perry_weather/perry_stacked_color_white_text.svg"
                                alt="Perry Weather Logo"
                                className="w-auto opacity-70 object-contain max-h-40"
                            />
                            {current.observation_time && (
                                <div className="text-lg text-neutral-500 whitespace-nowrap">
                                    Updated {moment(current.observation_time).format("LT")}
                                </div>
                            )}
                        </div>
                    </div>

                    {hasTempRange && (
                        <div className="flex flex-row items-center gap-4">
                            <span
                                className="text-3xl font-plot-open-sans text-neutral-300 shrink-0"
                                style={{ fontVariantNumeric: "tabular-nums" }}
                            >
                                {Math.round(today.low_temp)}°
                            </span>
                            <div className="relative flex-1 h-3 rounded-full bg-linear-to-r from-blue-500 to-red-500">
                                <div
                                    className="absolute top-1/2 h-7 w-7 rounded-full bg-white border-4 border-neutral-900"
                                    style={{ left: `${tempMarkerPct}%`, transform: "translate(-50%, -50%)" }}
                                />
                            </div>
                            <span
                                className="text-3xl font-plot-open-sans text-neutral-300 shrink-0"
                                style={{ fontVariantNumeric: "tabular-nums" }}
                            >
                                {Math.round(today.high_temp)}°
                            </span>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-[repeat(2,minmax(min-content,1fr))] gap-4">
                    {stats.map((stat) => (
                        <div
                            key={stat.label}
                            className="flex flex-row items-center justify-center gap-4 p-12 rounded-lg shadow-sm border bg-neutral-800 border-neutral-700"
                        >
                            <FontAwesomeIcon icon={stat.icon as IconProp} className="text-plot-yellow" size="4x" />
                            <div className="flex flex-col">
                                <div className="text-3xl uppercase tracking-wide text-neutral-400 font-semibold">
                                    {stat.label}
                                </div>
                                <div
                                    className="text-6xl font-semibold whitespace-nowrap"
                                    style={{ fontVariantNumeric: "tabular-nums" }}
                                >
                                    {stat.value}
                                </div>
                                {stat.subValue && (
                                    <div className="text-xl text-neutral-400 mt-1 whitespace-nowrap">
                                        {stat.subValue}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default DashboardCurrentConditions;
